import { groupBy, isArray } from "lodash";
import i18next from "src/i18n/config";
import { ListResponse } from "src/types";

import HttpService from "./httpService";
import RouteService from "./routeService";

export const getCountries = async () => {
    const endpoint = await RouteService.getAccountsRoute();
    const { data } = await HttpService.get<ListResponse<API.Country>>(endpoint + "locations");
    return isArray(data.list) ? data.list : [];
};

/**
 * The backend only has English names, so the name is taken from the browser by the country's ISO code.
 * The English name stays the stored value: profiles and billing addresses keep it, and the backend expects it.
 */
const createCountryNameFormatter = (language: string) => {
    let displayNames: Intl.DisplayNames | null = null;
    try {
        displayNames = new Intl.DisplayNames([language], { type: "region" });
    } catch {
        displayNames = null;
    }

    return (country: API.Country) => {
        try {
            return displayNames?.of(country.code.toUpperCase()) || country.country;
        } catch {
            return country.country;
        }
    };
};

export const getCountryDisplayName = (country: API.Country, language: string = i18next.language) =>
    createCountryNameFormatter(language)(country);

export const getCountryData = async (language: string = i18next.language) => {
    const countries = await getCountries();
    const getName = createCountryNameFormatter(language);
    const byName = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label, language);

    const phoneCodes = Object.entries(groupBy(countries, (country) => country.phone))
        .map(([phone, sharingCountries]) => ({
            value: phone,
            label: `${sharingCountries
                .map(getName)
                .sort((a, b) => a.localeCompare(b, language))
                .join(", ")} (${phone})`,
        }))
        .sort(byName);

    const countryList = countries
        .map((country) => ({
            value: country.country,
            label: getName(country),
        }))
        .sort(byName);

    const mossCountries = countries.filter((country) => country.usesMOSS).map((country) => country.country);

    return {
        phoneCodes,
        countryList,
        mossCountries,
    };
};
